import { useCallback, useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Check, X, Eye } from "lucide-react";
import api, { BACKEND_URL, getToken } from "../lib/api";
import { extractError } from "../lib/formatError";

const STATUSES = ["pending", "approved", "rejected"];

export default function AdminKyc() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (status) params.set("status", status);
      const { data } = await api.get(`/admin/kyc?${params}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-kyc">
      <Toaster theme="dark" position="top-center" />
      <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">KYC</div>
      <h1 className="font-display italic text-4xl text-[color:var(--ink)] mb-6">Identity queue ({total})</h1>

      <div className="flex gap-2 mb-5">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-[10px] tracking-widest uppercase border ${status === s ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
            data-testid={`kyc-tab-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}
      {loading ? <div className="text-[color:var(--muted)] text-sm">Loading…</div>
        : items.length === 0 ? <div className="text-[color:var(--muted)] text-sm italic">No submissions.</div>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((s) => (
              <div key={s.id} className="border border-[color:var(--line)] p-4" style={{ background: "var(--card)" }} data-testid={`kyc-card-${s.id}`}>
                <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-1">{s.doc_type}</div>
                <div className="text-sm text-[color:var(--ink)] break-all mb-1">{s.user_email}</div>
                <div className="text-[10px] text-[color:var(--muted)] mb-3">{new Date(s.submitted_at).toLocaleString()}</div>
                <StatusPill s={s.status} />
                {s.reject_reason && <div className="text-[10px] text-[color:var(--red)] mt-2">{s.reject_reason}</div>}
                <button
                  onClick={() => setSelected(s)}
                  className="mt-3 w-full py-2 border border-[color:var(--line)] hover:border-[color:var(--gold)] text-[10px] tracking-widest uppercase text-[color:var(--muted)] hover:text-[color:var(--gold)] flex items-center justify-center gap-2"
                  data-testid={`review-${s.id}`}
                >
                  <Eye className="w-3 h-3" /> Review
                </button>
              </div>
            ))}
          </div>
        )}

      {selected && (
        <ReviewModal
          sub={selected}
          onClose={() => setSelected(null)}
          onDone={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function ReviewModal({ sub, onClose, onDone }) {
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const approve = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/admin/kyc/${sub.id}/approve`);
      toast.success("KYC approved");
      onDone();
    } catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/admin/kyc/${sub.id}/reject`, { reason });
      toast.success("KYC rejected");
      onDone();
    } catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 overflow-auto" style={{ background: "rgba(6,4,3,0.9)", backdropFilter: "blur(4px)" }} data-testid="kyc-review-modal">
      <div className="w-full max-w-4xl border border-[color:var(--line)] p-6" style={{ background: "var(--card)" }}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)]">KYC review · {sub.doc_type}</div>
            <div className="font-display italic text-2xl text-[color:var(--gold)]">{sub.user_email}</div>
          </div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-[color:var(--muted)]" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <DocView label="ID · Front" url={sub.id_front_url} />
          <DocView label="ID · Back" url={sub.id_back_url} />
          <DocView label="Selfie" url={sub.selfie_url} />
        </div>

        {err && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{err}</div>}

        {sub.status !== "pending" ? (
          <div className="text-sm text-[color:var(--muted)]">Already {sub.status}.</div>
        ) : !rejectMode ? (
          <div className="flex gap-3">
            <button onClick={approve} disabled={busy} className="spin-btn flex-1" data-testid="kyc-approve-btn">
              {busy ? "…" : "Approve"}
            </button>
            <button onClick={() => setRejectMode(true)} disabled={busy} className="flex-1 py-3 border border-[color:var(--red)] text-[color:var(--red)] text-[11px] tracking-[0.25em] uppercase hover:bg-[color:var(--card)]" data-testid="kyc-reject-btn">
              Reject
            </button>
          </div>
        ) : (
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)] mb-2">Reason for rejection *</div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="bet-input text-xs w-full mb-3" data-testid="kyc-reject-reason" />
            <div className="flex gap-3">
              <button onClick={() => setRejectMode(false)} className="flex-1 py-3 border border-[color:var(--line)] text-[color:var(--muted)] text-[11px] tracking-widest uppercase">Cancel</button>
              <button onClick={reject} disabled={busy || !reason.trim()} className="flex-1 py-3 border border-[color:var(--red)] text-[color:var(--red)] text-[11px] tracking-[0.25em] uppercase disabled:opacity-50" data-testid="kyc-reject-confirm">
                {busy ? "…" : "Confirm rejection"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DocView({ label, url }) {
  const [blob, setBlob] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!url) return;
    let mounted = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}${url}`, { headers: { Authorization: `Bearer ${getToken()}` } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const b = await r.blob();
        if (!mounted) return;
        setBlob(URL.createObjectURL(b));
      } catch (e) {
        if (mounted) setErr(e.message);
      }
    })();
    return () => {
      mounted = false;
      if (blob) URL.revokeObjectURL(blob);
    };
  }, [url]); // eslint-disable-line

  return (
    <div className="border border-[color:var(--line)]" style={{ background: "var(--shell-2)" }}>
      <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] px-2 py-1 border-b border-[color:var(--line)]">{label}</div>
      <div className="aspect-video flex items-center justify-center">
        {!url ? (
          <div className="text-[10px] text-[color:var(--muted)]">Not provided</div>
        ) : err ? (
          <div className="text-[10px] text-[color:var(--red)]">{err}</div>
        ) : !blob ? (
          <div className="text-[10px] text-[color:var(--muted)] animate-pulse">Loading…</div>
        ) : (
          <img src={blob} alt={label} className="w-full h-full object-contain" />
        )}
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = { pending: "muted", approved: "gold", rejected: "red" };
  const c = map[s] || "muted";
  return <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 border" style={{ color: `var(--${c})`, borderColor: `var(--${c})` }}>{s}</span>;
}
